import React from "react";
import {
  DialogTitle,
  IconButton,
  Typography,
  LinearProgress,
  Stack,
  Fade,
  Box,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

interface DialogHeaderProps {
  title: string;
  loading?: boolean;
  onClose?: () => void;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  /**
   * Заголовок в одну строку с многоточием (полный текст в подсказке
   * браузера), кнопки не сжимаются; уже md кнопки уходят отдельной
   * строкой на всю ширину. Для полноэкранных редакторов с длинным
   * названием и многими кнопками. По умолчанию заголовок переносится
   * (Review I1: раньше это было единственным и включённым всегда
   * поведением, из-за чего длинное название переносило и кнопки).
   */
  singleLineTitle?: boolean;
  /**
   * Строка под заголовком (чипы статуса и т.п.), в той же колонке, что
   * заголовок, слева от кнопок.
   */
  subtitle?: React.ReactNode;
  /**
   * С singleLineTitle: полный заголовок в подсказке MUI вместо подсказки
   * браузера.
   */
  titleTooltip?: boolean;
}

export const DialogHeader: React.FC<DialogHeaderProps> = ({
  title,
  onClose,
  actions,
  children,
  loading,
  singleLineTitle = false,
  subtitle,
  titleTooltip = false,
}) => {
  const { t } = useTranslation();
  // Доступное имя крестика на языке интерфейса (финальное ревью, минор 14):
  // раньше был жёсткий английский "close". Ключ уже есть в сиде партнёра
  // (ru «Закрыть», en «Close»); у админки своего словаря нет, она целиком
  // русская, поэтому defaultValue, как у плеера в shared. Видимое не
  // меняется: у иконки нет подписи.
  const closeLabel = t("shared.player.close", { defaultValue: "Закрыть" });
  return (
    <Box position="relative">
      <DialogTitle
        sx={{ padding: 2 }}
        position="relative"
        bgcolor="background.paper"
      >
        <Stack
          width={1}
          direction="row"
          alignItems="center"
          spacing={1}
          useFlexGap={singleLineTitle}
          sx={
            singleLineTitle
              ? { flexWrap: { xs: "wrap", md: "nowrap" } }
              : undefined
          }
        >
          {onClose && (
            <IconButton
              aria-label={closeLabel}
              component="a"
              href="#close"
              onClick={(e) => {
                e.preventDefault();
                onClose();
              }}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          )}
          {subtitle === undefined && !titleTooltip ? (
            <Typography
              variant="h6"
              component="div"
              noWrap={singleLineTitle}
              title={singleLineTitle ? title : undefined}
              sx={
                singleLineTitle
                  ? { flex: "1 1 0", minWidth: 0 }
                  : { flexGrow: 1 }
              }
            >
              {title}
            </Typography>
          ) : (
            <Stack
              spacing={0.75}
              sx={{ flex: "1 1 0", minWidth: 0, alignSelf: "center" }}
            >
              <Tooltip
                title={titleTooltip && singleLineTitle ? title : ""}
                placement="bottom-start"
              >
                <Typography
                  variant="h6"
                  component="div"
                  noWrap={singleLineTitle}
                  title={singleLineTitle && !titleTooltip ? title : undefined}
                  sx={{ minWidth: 0 }}
                >
                  {title}
                </Typography>
              </Tooltip>
              {subtitle}
            </Stack>
          )}
          {actions && (
            <Stack
              direction="row"
              gap={1}
              sx={
                singleLineTitle
                  ? {
                      flexShrink: 0,
                      flexWrap: { xs: "wrap", md: "nowrap" },
                      width: { xs: 1, md: "auto" },
                    }
                  : undefined
              }
            >
              {actions}
            </Stack>
          )}
        </Stack>
        {children && <Box mt={2}>{children}</Box>}
        <Box position="absolute" left={0} right={0} bottom={0}>
          <Fade in={!!loading}>
            <LinearProgress sx={{ height: 4 }} />
          </Fade>
        </Box>
      </DialogTitle>
    </Box>
  );
};
